import {
    EnumButtonName,
    EnumFeature,
    EnumScreenName,
    EventProperties,
} from '@calm/evexia-analytics';
import { ChLink } from '@rhg/components/ch-link';
import { FC, MouseEvent, Ref, forwardRef, useMemo } from 'react';
import { TrackFn, useTracking } from '../..';
import { useAnalyticsTitle } from '../use-analytics-title';

export type ButtonAnalyticsProps<T> = T & {
    /**
     * Analytics button name
     */
    trackButton: EnumButtonName;
    /**
     * Analytics feature name (defaults to the feature for the current route)
     */
    trackFeature?: EnumFeature;
    /**
     * Analytics screen name (defaults to screen for the current route)
     */
    trackScreen?: EnumScreenName;
    /**
     * Any additional props
     */
    trackProps?: EventProperties;

    /**
     * Track analytics button click events after processing the provided
     * onClick handler. This is useful if you need to set super-properties
     * before the event is sent.
     */
    trackAfterOnClick?: boolean;

    /**
     * Appends a unique identified to differentiate buttons with the
     * same tracking name. (used for e2e tests)
     */
    testId?: string;

    /**
     * Pass true to immediately post events to analytics destination
     */
    trackAndFlushEvents?: boolean;
};

export type ButtonWithoutAnalyticsProps<T> = T & {
    skipTracking: boolean;
};

export const clickEventName = 'Button : Clicked';

export interface OnAnalyticsClickHandler {
    e?: MouseEvent<HTMLElement>;
    onClick?: (...args: any[]) => void;
    track: TrackFn;
    flush?: () => Promise<void> | void;
    currentFeature: EnumFeature;
    currentScreenName: EnumScreenName;
}

export const onAnalyticsClickHandler = ({
    trackAfterOnClick,
    onClick,
    track,
    flush,
    trackButton,
    trackFeature,
    trackScreen,
    trackProps,
    currentFeature,
    currentScreenName,
    e,
    trackAndFlushEvents = false,
}: ButtonAnalyticsProps<OnAnalyticsClickHandler>) => {
    if (trackAfterOnClick) {
        onClick?.(e);
    }

    const screenName = trackScreen ?? currentScreenName;

    // Surface-level context (e.g. home PackClass / RowIndex / RowItemIndex)
    // is auto-attached by `track` from the enclosing
    // `AnalyticsEventPropsProvider`; nothing to merge here. Use
    // `<AnalyticsEventPropsProvider clear>` around the button to opt out
    // when the click event shouldn't carry inherited context.
    track(`${clickEventName}`, {
        ButtonName: trackButton,
        Feature: trackFeature ?? currentFeature,
        ScreenName: screenName,
        ...(trackProps ?? {}),
    })
        .then(() => {
            if (trackAndFlushEvents && flush) {
                return Promise.resolve(flush());
            }
        })
        .catch(() => {});
    if (!trackAfterOnClick) {
        onClick?.(e);
    }
};

export function withButtonAnalytics<
    T extends { onClick?: (...args: any[]) => void }
>(Component: FC<T>) {
    function AnalyticsButton(
        props: ButtonAnalyticsProps<T> & { forwardedRef: Ref<unknown> }
    ) {
        const {
            forwardedRef,
            trackButton,
            trackScreen,
            trackFeature,
            trackProps,
            testId,
            trackAfterOnClick,
            onClick,
            trackAndFlushEvents,
            ...others
        } = props as ButtonAnalyticsProps<T> & { forwardedRef: Ref<unknown> };
        const {
            track,
            flush,
            state: { currentFeature, currentScreenName },
        } = useTracking();

        const handleOnClick = (e: MouseEvent<HTMLElement>) =>
            onAnalyticsClickHandler({
                trackAfterOnClick,
                onClick,
                track,
                flush,
                trackButton,
                trackFeature,
                trackScreen,
                trackProps,
                currentFeature,
                currentScreenName,
                trackAndFlushEvents: trackAndFlushEvents,
                e,
            });

        const { analyticsTitleProps } = useAnalyticsTitle(
            `${trackButton}  |  Click${trackButton}`
        );

        return useMemo(
            () => (
                <Component
                    data-testid={`BUTTON_${trackButton}${
                        testId ? '_' + testId : ''
                    }`}
                    {...(others as typeof props)}
                    {...analyticsTitleProps}
                    ref={forwardedRef}
                    onClick={handleOnClick}
                />
            ),
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [forwardedRef, others, analyticsTitleProps, trackButton]
        );
    }

    /**
     * HOC components do not forward `ref` by default so we setup the `forwardedRef` prop
     * and pass it to the nested function component and map it back to `ref`
     */
    // TODO: EVE-6055 figure out the props mismatch
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return forwardRef(function ForwardedAnalyticsButton(
        props: ButtonAnalyticsProps<T> | ButtonWithoutAnalyticsProps<T>,
        ref
    ) {
        // Use <a> tag for download actions
        const linkComponentProps =
            'href' in props && props.href
                ? 'download' in props
                    ? { component: 'a' }
                    : { component: ChLink }
                : {};

        // If skipTracking is present in props
        if ('skipTracking' in props) {
            const skipTrackingValue = props.skipTracking;

            // If skipTracking is true, remove both skipTracking and all analytics props
            if (skipTrackingValue === true) {
                const {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    skipTracking,
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    trackButton: _trackButton,
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    trackProps: _trackProps,
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    trackFeature: _trackFeature,
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    trackScreen: _trackScreen,
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    trackAfterOnClick: _trackAfterOnClick,
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    testId: _testId,
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    trackAndFlushEvents: _trackAndFlushEvents,
                    ...others
                } = props as ButtonAnalyticsProps<T> &
                    ButtonWithoutAnalyticsProps<T>;
                return (
                    <Component
                        {...(others as unknown as T)}
                        {...linkComponentProps}
                        ref={ref}
                    />
                );
            }

            // If skipTracking is false or any other value, remove only skipTracking
            const {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                skipTracking,
                ...others
            } = props as ButtonAnalyticsProps<T> &
                ButtonWithoutAnalyticsProps<T>;
            return (
                <AnalyticsButton
                    {...(others as unknown as ButtonAnalyticsProps<T>)}
                    {...linkComponentProps}
                    forwardedRef={ref}
                />
            );
        }

        // No skipTracking prop, use normal analytics flow
        return (
            <AnalyticsButton
                {...props}
                {...linkComponentProps}
                forwardedRef={ref}
            />
        );
    });
}
